import { useFormContext, Controller } from 'react-hook-form';
import ReactDatePicker from 'react-datepicker';
import _ from 'lodash';
import 'react-datepicker/dist/react-datepicker.css';
import { useState } from 'react';
import { createPortal } from 'react-dom';

const DatePicker = ({
    label,
    name,
    required,
    placeholder,
    className = '',
    isClearable = true,
    showTimeSelect,
    showTimeSelectOnly,
    dateFormat,
    shouldUnregister,
    excludeTimes,
    filterTime,
    maxDate,
    minDate,
    onChangeHandler,
    readOnly,
    padding = 'px-4 py-2',
    withPortal = false,
    popperPlacement
}) => {
    const {
        formState: { errors },
        control,
    } = useFormContext();

    const [isOpen, setIsOpen] = useState(false);

    const getDateFormat = () => {
        let format = 'dd/MM/yyyy';
        if (showTimeSelect) {
            if (showTimeSelectOnly) {
                format = 'HH:mm a';
            } else if (dateFormat) {
                format = dateFormat;
            } else {
                format = 'dd/MM/yyyy HH:mm';
            }
        }
        return format;
    };

    const getMonthDropdownStyle = () => {
        return withPortal
            ? {
                minWidth: '110px',
                minHeight: '38px',
                fontSize: '1rem',
                fontWeight: 500,
                height: '38px'
            }
            : { maxHeight: '10vh' };
    };

    const getYearDropdownStyle = () => {
        return withPortal
            ? {
                minWidth: '86px',
                minHeight: '38px',
                fontSize: '1rem',
                fontWeight: 500,
                height: '38px'
            }
            : { maxHeight: '8rem' };
    };

    const getMonthDropdownClass = () =>
        `form-select border border-scogoccc rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-scogoprimary${withPortal ? ' text-base font-medium' : ' text-sm'}`;

    const getYearDropdownClass = () =>
        `form-select border border-scogoccc rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-scogoprimary min-w-[50px]${withPortal ? ' text-base font-medium' : ' text-sm'}`;

    const formatDisplayDate = (date) => {
        if (!date) return '';
        try {
            const d = date instanceof Date ? date : new Date(date);
            if (isNaN(d.getTime())) return '';

            const day = String(d.getDate()).padStart(2, '0');
            const month = String(d.getMonth() + 1).padStart(2, '0');
            const year = d.getFullYear();
            return `${day}/${month}/${year}`;
        } catch (e) {
            console.error('Error formatting date:', e);
            return '';
        }
    };

    const CustomInput = ({ value, onClick }) => (
        <div className="relative w-full">
            <div className="absolute left-2 top-2 z-10 pointer-events-none">
                <span className='material-icons-outlined text-scogoorange font-normal text-font16 inputFontSize'>event</span>
            </div>
            <input
                type="text"
                value={formatDisplayDate(value)}
                onClick={onClick}
                placeholder={placeholder}
                readOnly
                className={`border inputFontSize ${readOnly ? 'border-scogogray bg-scogof2' : 'border-scogoccc focus:border-scogoprimary bg-white'
                    } w-full pl-10 rounded-6px text-scogo99 font-normal text-font09 md:text-font11 cursor-pointer`}
            />
        </div>
    );

    return (
        <div className={`${padding} ${className}`}>
            {label && (
                <label>
                    <span className='text-scogo99 font-normal text-font09 md:text-font11 labelFotnSize'>
                        {label} {required && <span className='text-scogoorange'>*</span>}{' '}
                    </span>
                </label>
            )}
            <div className='relative'>
                <Controller
                    key={name}
                    control={control}
                    name={name}
                    rules={{ required: { value: required, message: 'Required' } }}
                    shouldUnregister={shouldUnregister}
                    render={({ field: { onChange, value, onBlur } }) => (
                        <>
                            {withPortal ? (
                                <>
                                    <CustomInput
                                        value={value}
                                        onClick={() => !readOnly && setIsOpen(true)}
                                    />
                                    {isOpen && createPortal(
                                        <div
                                            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-50"
                                            onClick={() => setIsOpen(false)}
                                        >
                                            <div
                                                className="bg-white rounded-lg shadow-2xl p-4"
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                <ReactDatePicker
                                                    inline
                                                    selected={value ? (value instanceof Date ? value : new Date(value)) : null}
                                                    onChange={(date) => {
                                                        onChange(date);
                                                        onBlur();
                                                        if (onChangeHandler) {
                                                            onChangeHandler(date);
                                                        }
                                                        setIsOpen(false);
                                                    }}
                                                    dateFormat={getDateFormat()}
                                                    showTimeSelect={showTimeSelect}
                                                    showTimeSelectOnly={showTimeSelectOnly}
                                                    excludeTimes={excludeTimes}
                                                    filterTime={filterTime}
                                                    maxDate={maxDate}
                                                    minDate={minDate}
                                                    showMonthDropdown
                                                    showYearDropdown
                                                    scrollableYearDropdown
                                                    yearDropdownItemNumber={15}
                                                    dropdownMode="select"
                                                    renderMonthContent={(month, shortMonth) => (
                                                        <span className="px-2 py-1 hover:bg-scogoprimary hover:text-white rounded transition-colors">
                                                            {shortMonth}
                                                        </span>
                                                    )}
                                                    renderYearContent={(year) => (
                                                        <span className="px-2 py-1 hover:bg-scogoprimary hover:text-white rounded transition-colors">
                                                            {year}
                                                        </span>
                                                    )}
                                                    renderCustomHeader={({
                                                        date,
                                                        changeYear,
                                                        changeMonth,
                                                        decreaseMonth,
                                                        increaseMonth,
                                                        prevMonthButtonDisabled,
                                                        nextMonthButtonDisabled,
                                                    }) => (
                                                        <div className="flex items-center justify-between px-2 py-2">
                                                            <button
                                                                onClick={decreaseMonth}
                                                                disabled={prevMonthButtonDisabled}
                                                                type="button"
                                                                className={`p-1 rounded-full hover:bg-gray-200 ${prevMonthButtonDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                            >
                                                                <span className="material-icons-outlined text-scogo99">chevron_left</span>
                                                            </button>
                                                            <div className="flex space-x-2">
                                                                <select
                                                                    value={date.getMonth()}
                                                                    onChange={({ target: { value } }) => changeMonth(value)}
                                                                    className={getMonthDropdownClass()}
                                                                    style={getMonthDropdownStyle()}
                                                                >
                                                                    {Array.from({ length: 12 }, (_, i) => (
                                                                        <option key={i} value={i}>
                                                                            {new Date(date.getFullYear(), i, 1).toLocaleString('default', { month: 'long' })}
                                                                        </option>
                                                                    ))}
                                                                </select>
                                                                <select
                                                                    value={date.getFullYear()}
                                                                    onChange={({ target: { value } }) => changeYear(value)}
                                                                    className={getYearDropdownClass()}
                                                                    style={getYearDropdownStyle()}
                                                                >
                                                                    {Array.from({ length: 56 }, (_, i) => {
                                                                        const year = date.getFullYear() - 50 + i;
                                                                        return (
                                                                            <option key={i} value={year}>
                                                                                {year}
                                                                            </option>
                                                                        );
                                                                    })}
                                                                </select>
                                                            </div>
                                                            <button
                                                                onClick={increaseMonth}
                                                                disabled={nextMonthButtonDisabled}
                                                                type="button"
                                                                className={`p-1 rounded-full hover:bg-gray-200 ${nextMonthButtonDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                            >
                                                                <span className="material-icons-outlined text-scogo99">chevron_right</span>
                                                            </button>
                                                        </div>
                                                    )}
                                                />
                                                {isClearable && value && (
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            onChange(null);
                                                            onBlur();
                                                            if (onChangeHandler) {
                                                                onChangeHandler(null);
                                                            }
                                                            setIsOpen(false);
                                                        }}
                                                        className="mt-2 w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 rounded border border-red-200"
                                                    >
                                                        Clear
                                                    </button>
                                                )}
                                            </div>
                                        </div>,
                                        document.body
                                    )}
                                </>
                            ) : (
                                <div className="relative w-full">
                                    <div className="absolute left-2 top-2 z-10 pointer-events-none">
                                        <span className='material-icons-outlined text-scogoorange font-normal text-font16 inputFontSize'>event</span>
                                    </div>
                                    <ReactDatePicker
                                        className={`border inputFontSize ${readOnly ? 'border-scogogray bg-scogof2' : 'border-scogoccc focus:border-scogoprimary bg-white'
                                            } w-full pl-10 rounded-6px text-scogo99 font-normal text-font09 md:text-font11`}
                                        placeholderText={placeholder}
                                        isClearable={isClearable}
                                        dateFormat={getDateFormat()}
                                        name={name}
                                        selected={value}
                                        onChange={(event) => {
                                            onChange(event);
                                            if (onChangeHandler) {
                                                onChangeHandler(event);
                                            }
                                        }}
                                        showTimeSelect={showTimeSelect}
                                        showTimeSelectOnly={showTimeSelectOnly}
                                        popperProps={{ strategy: 'fixed' }}
                                        excludeTimes={excludeTimes}
                                        filterTime={filterTime}
                                        maxDate={maxDate ? maxDate : undefined}
                                        minDate={minDate ? minDate : undefined}
                                        readOnly={readOnly}
                                        showMonthDropdown
                                        showYearDropdown
                                        scrollableYearDropdown
                                        yearDropdownItemNumber={15}
                                        popperModifiers={{
                                            preventOverflow: {
                                                enabled: true,
                                            },
                                        }}
                                        popperPlacement={popperPlacement || "bottom-start"}
                                        dropdownMode="select"
                                        renderMonthContent={(month, shortMonth) => (
                                            <span className="px-2 py-1 hover:bg-scogoprimary hover:text-white rounded transition-colors">
                                                {shortMonth}
                                            </span>
                                        )}
                                        renderYearContent={(year) => (
                                            <span className="px-2 py-1 hover:bg-scogoprimary hover:text-white rounded transition-colors">
                                                {year}
                                            </span>
                                        )}
                                        renderCustomHeader={({
                                            date,
                                            changeYear,
                                            changeMonth,
                                            decreaseMonth,
                                            increaseMonth,
                                            prevMonthButtonDisabled,
                                            nextMonthButtonDisabled,
                                        }) => (
                                            <div className="flex items-center justify-between px-2 py-2">
                                                <button
                                                    onClick={decreaseMonth}
                                                    disabled={prevMonthButtonDisabled}
                                                    type="button"
                                                    className={`p-1 rounded-full hover:bg-gray-200 ${prevMonthButtonDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                >
                                                    <span className="material-icons-outlined text-scogo99">chevron_left</span>
                                                </button>
                                                <div className="flex space-x-2">
                                                    <select
                                                        value={date.getMonth()}
                                                        onChange={({ target: { value } }) => changeMonth(value)}
                                                        className="form-select text-sm border border-scogoccc rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-scogoprimary"
                                                        style={{ maxHeight: '10vh' }}
                                                    >
                                                        {Array.from({ length: 12 }, (_, i) => (
                                                            <option key={i} value={i}>
                                                                {new Date(date.getFullYear(), i, 1).toLocaleString('default', { month: 'long' })}
                                                            </option>
                                                        ))}
                                                    </select>
                                                    <select
                                                        value={date.getFullYear()}
                                                        onChange={({ target: { value } }) => changeYear(value)}
                                                        className="form-select text-sm border border-scogoccc rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-scogoprimary min-w-[50px] "
                                                        style={{ maxHeight: '8rem' }}
                                                    >
                                                        {Array.from({ length: 56 }, (_, i) => {
                                                            const year = date.getFullYear() - 50 + i;
                                                            return (
                                                                <option key={i} value={year}>
                                                                    {year}
                                                                </option>
                                                            );
                                                        })}
                                                    </select>
                                                </div>
                                                <button
                                                    onClick={increaseMonth}
                                                    disabled={nextMonthButtonDisabled}
                                                    type="button"
                                                    className={`p-1 rounded-full hover:bg-gray-200 ${nextMonthButtonDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                                                >
                                                    <span className="material-icons-outlined text-scogo99">chevron_right</span>
                                                </button>
                                            </div>
                                        )}
                                    />
                                </div>
                            )}
                        </>
                    )}
                />
                {_.get(errors, name) && <span className='text-scogoclosed text-font08 font-normal'>{_.get(errors, name).message}</span>}
            </div>
        </div>
    );
};

export default DatePicker;
